import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import SendIcon from '@mui/icons-material/Send';
import toast from 'react-hot-toast';
import * as moment from 'moment';
import { formateurSurveyApi } from '../../../api/formateurSurveyApi';
import { grey } from '../../../theme/tones';

/**
 * Bouton « Informer formateur » de la fiche session.
 *
 * Deux courriers, choisis dans un menu déroulant :
 *
 *  1. « Questionnaire satisfaction + liste de présence » — le questionnaire de
 *     satisfaction correspondant au format de la formation, la liste de présence
 *     en pièce jointe. C'est aussi ce que le cron envoie automatiquement la
 *     veille de la formation.
 *  2. « Maintien de la formation » — confirme au formateur que la session est
 *     bien maintenue : horaires, et lien Zoom ou lieu selon le format. Jamais
 *     automatique, et présenté en aperçu avant l'envoi.
 *
 * Les deux s'adressent au même destinataire : le formateur de la session,
 * résolu depuis sa fiche intervenant.
 */
const SessionInformerFormateurButton = ({ sessionId, sx, disabled }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // Questionnaire de satisfaction
  const [surveyOpen, setSurveyOpen] = useState(false);
  const [plan, setPlan] = useState(null);
  const [email, setEmail] = useState('');

  // Maintien de la formation
  const [maintienOpen, setMaintienOpen] = useState(false);
  const [maintien, setMaintien] = useState(null);
  const [maintienEmail, setMaintienEmail] = useState('');
  const [preview, setPreview] = useState(true);

  const closeMenu = () => setAnchorEl(null);

  const handleOpenSurvey = async () => {
    closeMenu();
    setLoading(true);
    setPlan(null);
    try {
      const data = await formateurSurveyApi.getPlan(sessionId, localStorage.getItem('accessToken'));

      // Sans formateur ou sans adresse, on renvoie vers la donnée à compléter
      // plutôt que d'ouvrir un dialogue inutilisable.
      if (!data || !data.available || !data.recipient) {
        toast.error(
          (data && data.message) || "Aucun questionnaire formateur à envoyer pour cette session.",
          { duration: 8000 }
        );
        setLoading(false);
        return;
      }

      setPlan(data);
      setEmail(data.recipient ? data.recipient.email : '');
      setSurveyOpen(true);
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Impossible de préparer l'envoi au formateur.", { duration: 10000 });
    }
    setLoading(false);
  };

  const handleOpenMaintien = async () => {
    closeMenu();
    setLoading(true);
    setMaintien(null);
    try {
      const data = await formateurSurveyApi.getMaintienPlan(sessionId, localStorage.getItem('accessToken'));

      // Le back refuse tant qu'il manque le lien de connexion ou le lieu : son
      // message dit quoi compléter dans la fiche session.
      if (!data || !data.available || !data.recipient) {
        toast.error(
          (data && data.message) || "Impossible de préparer le mail de maintien pour cette session.",
          { duration: 8000 }
        );
        setLoading(false);
        return;
      }

      setMaintien(data);
      setMaintienEmail(data.recipient ? data.recipient.email : '');
      setPreview(true);
      setMaintienOpen(true);
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Impossible de préparer le mail de maintien.", { duration: 10000 });
    }
    setLoading(false);
  };

  const handleSendSurvey = async () => {
    setSending(true);
    try {
      const res = await formateurSurveyApi.send(sessionId, email, localStorage.getItem('accessToken'));
      if (res && res.status === 200) {
        toast.success(res.message || 'Questionnaire envoyé au formateur !');
        setSurveyOpen(false);
      } else {
        toast.error((res && res.message) || "L'envoi a échoué.");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Une erreur est survenue lors de l'envoi !", { duration: 10000 });
    }
    setSending(false);
  };

  const handleSendMaintien = async () => {
    setSending(true);
    try {
      const res = await formateurSurveyApi.sendMaintien(sessionId, maintienEmail, localStorage.getItem('accessToken'));
      if (res && res.status === 200) {
        toast.success(res.message || 'Mail de maintien envoyé au formateur !');
        setMaintienOpen(false);
      } else {
        toast.error((res && res.message) || "L'envoi a échoué.");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Une erreur est survenue lors de l'envoi !", { duration: 10000 });
    }
    setSending(false);
  };

  const answered = plan && plan.answered_at;
  const details = maintien && maintien.details;

  /** Une ligne du récapitulatif du maintien. */
  const detailRow = (label, value) => (
    <Box sx={{ display: 'flex', gap: 1.5, py: 0.4 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 92, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <>
      <Button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={sx}
        variant="outlined"
        disabled={disabled || loading || sending}
        startIcon={
          loading || sending
            ? <CircularProgress size={14} />
            : <MarkEmailReadOutlinedIcon fontSize="small" />
        }
        endIcon={<ArrowDropDownIcon />}
      >
        Informer formateur
      </Button>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        <MenuItem onClick={handleOpenSurvey} sx={{ py: 1.25 }}>
          <ListItemIcon>
            <AssignmentTurnedInOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Questionnaire satisfaction + liste de présence"
            secondary="Le questionnaire à remplir après la formation, liste de présence jointe"
            primaryTypographyProps={{ fontWeight: 600 }}
            secondaryTypographyProps={{ fontSize: 12 }}
          />
        </MenuItem>
        <MenuItem onClick={handleOpenMaintien} sx={{ py: 1.25 }}>
          <ListItemIcon>
            <EventAvailableOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Maintien de la formation"
            secondary="Confirme la tenue de la session — lien de connexion ou lieu selon le format"
            primaryTypographyProps={{ fontWeight: 600 }}
            secondaryTypographyProps={{ fontSize: 12 }}
          />
        </MenuItem>
      </Menu>

      {/* ── Questionnaire de satisfaction ─────────────────────────────────── */}
      <Dialog open={surveyOpen} onClose={() => setSurveyOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Questionnaire de satisfaction</DialogTitle>
        <DialogContent>
          {plan && plan.available && (
            <>
              {answered && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {`Le formateur a déjà répondu le ${moment(plan.answered_at).format('DD/MM/YYYY')}.
                    Un nouvel envoi ne lui permettra pas de répondre une seconde fois.`}
                </Alert>
              )}

              <Typography variant="body2" sx={{ mb: 2 }}>
                Le formateur va recevoir le questionnaire de satisfaction à remplir
                à l&apos;issue de la formation, avec la liste de présence en pièce jointe.
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip size="small" label={plan.questionnaire.scope} color="primary" variant="outlined" />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {plan.questionnaire.name}
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                {plan.session.formation_name}
                {' · session n°'}
                {plan.session.number_session}
              </Typography>

              <Divider sx={{ mb: 2 }} />

              <TextField
                autoFocus
                fullWidth
                label="Adresse e-mail du formateur"
                helperText={`Formateur de la session : ${plan.recipient.name}`}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSurveyOpen(false)} color="primary">
            Annuler
          </Button>
          <Button
            onClick={handleSendSurvey}
            color="primary"
            variant="contained"
            disabled={sending || !email}
            startIcon={sending ? <CircularProgress size={14} /> : <SendIcon />}
          >
            Envoyer
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Maintien de la formation ──────────────────────────────────────── */}
      <Dialog open={maintienOpen} onClose={() => setMaintienOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Maintien de la formation</DialogTitle>
        <DialogContent>
          {maintien && maintien.available && (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Le formateur va recevoir la confirmation que sa session est bien maintenue.
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Chip size="small" label={maintien.scope} color="primary" variant="outlined" />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {maintien.session.formation_name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {`· session n°${maintien.session.number_session}`}
                </Typography>
              </Box>

              <Box
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: '12px',
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  backgroundColor: 'action.hover'
                }}
              >
                {detailRow('Date', details.date)}
                {detailRow('Horaires', details.horaires)}
                {details.lieu && detailRow('Lieu', details.lieu)}
                {details.lien && detailRow(`Lien ${details.lien_nom}`, details.lien)}
              </Box>

              <TextField
                fullWidth
                label="Adresse e-mail du formateur"
                helperText={`Formateur de la session : ${maintien.recipient.name}`}
                value={maintienEmail}
                onChange={(e) => setMaintienEmail(e.target.value)}
                type="email"
                sx={{ mb: 2 }}
              />

              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase' }}>
                    Objet
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                    {maintien.subject}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  onClick={() => setPreview(!preview)}
                  startIcon={preview ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                  sx={{ flexShrink: 0 }}
                >
                  {preview ? "Masquer l'aperçu" : "Visualiser l'e-mail"}
                </Button>
              </Box>

              {preview && (
                <Box
                  component="iframe"
                  title="Aperçu de l'e-mail de maintien"
                  srcDoc={maintien.html}
                  sx={{
                    width: '100%',
                    height: 460,
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: '12px',
                    backgroundColor: 'action.hover'
                  }}
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMaintienOpen(false)} color="primary">
            Annuler
          </Button>
          <Button
            onClick={handleSendMaintien}
            color="primary"
            variant="contained"
            disabled={sending || !maintienEmail}
            startIcon={sending ? <CircularProgress size={14} /> : <SendIcon />}
          >
            Envoyer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

SessionInformerFormateurButton.propTypes = {
  sessionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  sx: PropTypes.object,
  disabled: PropTypes.bool,
};

export default SessionInformerFormateurButton;
